import React, { useState, useEffect, useRef } from 'react';
import { Panel, Button } from '../../components/design';
import Icon from '../../components/Icon';

const SSH_FONT_SIZES = { xs: 12, sm: 14, base: 16 };

export default function SshTerminalTab({ serverId, server }) {
  const [sshConnected, setSshConnected] = useState(false);
  const [sshOutput, setSshOutput] = useState('');
  const [sshInput, setSshInput] = useState('');
  const [sshConnecting, setSshConnecting] = useState(false);
  const [sshError, setSshError] = useState('');
  const [sshHistory, setSshHistory] = useState([]);
  const [sshHistoryIndex, setSshHistoryIndex] = useState(-1);
  const [sshFontSize, setSshFontSize] = useState('sm');
  const [sshAutoScroll, setSshAutoScroll] = useState(true);
  const [sshConnectedAt, setSshConnectedAt] = useState(null);
  const [sshConnectionTime, setSshConnectionTime] = useState('');

  const wsRef = useRef(null);
  const sshTermRef = useRef(null);
  const sshInputRef = useRef(null);
  const sshTimerRef = useRef(null);

  const connectSsh = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setSshConnecting(true);
    setSshError('');
    setSshOutput('');
    setSshHistory([]);
    setSshHistoryIndex(-1);
    setSshConnectionTime('');
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/api/servers/${serverId}/ssh`);
    wsRef.current = ws;
    const connTimeout = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.close();
        setSshConnecting(false);
        setSshError('Connection timed out after 15 seconds. Check that the server is reachable and SSH credentials are correct.');
      }
    }, 15000);
    ws.onopen = () => {
      clearTimeout(connTimeout);
      setSshConnecting(false);
      setSshConnected(true);
      setSshConnectedAt(Date.now());
      setSshOutput('Connected to ' + (server?.name || serverId) + '\n');
    };
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'output') {
          setSshOutput(prev => prev + msg.data);
        } else if (msg.type === 'error') {
          setSshOutput(prev => prev + '\n[Error] ' + msg.data + '\n');
          setSshError(msg.data);
        } else if (msg.type === 'disconnected') {
          setSshConnected(false);
          setSshOutput(prev => prev + '\nSession ended.\n');
        }
      } catch (parseErr) {
        setSshOutput(prev => prev + '\n[Parse error: ' + parseErr.message + ']\n');
      }
    };
    ws.onerror = () => {
      clearTimeout(connTimeout);
      setSshConnecting(false);
      setSshError('WebSocket connection failed. Ensure the API server is running and reachable.');
    };
    ws.onclose = (event) => {
      clearTimeout(connTimeout);
      setSshConnecting(false);
      setSshConnected(false);
      setSshConnectedAt(null);
      setSshConnectionTime('');
      if (event.code !== 1000 && event.code !== 1005) {
        setSshError('Disconnected (code: ' + event.code + '). ' + (event.reason || ''));
      }
    };
  };

  const sendSshCommand = (cmd) => {
    if (!cmd || !cmd.trim()) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      setSshOutput(prev => prev + '$ ' + cmd + '\n');
      wsRef.current.send(JSON.stringify({ type: 'input', data: cmd + '\n' }));
      setSshHistory(prev => [...prev, cmd]);
      setSshHistoryIndex(-1);
      setSshInput('');
      if (sshInputRef.current) sshInputRef.current.focus();
    }
  };

  const disconnectSsh = () => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setSshConnected(false);
    setSshConnectedAt(null);
    setSshConnectionTime('');
    setSshOutput(prev => prev + '\nDisconnected.\n');
  };

  const handleSshKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendSshCommand(sshInput);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (sshHistory.length > 0) {
        const newIndex = sshHistoryIndex === -1 ? sshHistory.length - 1 : Math.max(0, sshHistoryIndex - 1);
        setSshHistoryIndex(newIndex);
        setSshInput(sshHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (sshHistoryIndex !== -1) {
        const newIndex = sshHistoryIndex + 1;
        if (newIndex >= sshHistory.length) {
          setSshHistoryIndex(-1);
          setSshInput('');
        } else {
          setSshHistoryIndex(newIndex);
          setSshInput(sshHistory[newIndex]);
        }
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (wsRef.current && sshConnected) {
        wsRef.current.send(JSON.stringify({ type: 'input', data: '\t' }));
      }
    }
  };

  useEffect(() => {
    if (sshAutoScroll && sshTermRef.current) {
      sshTermRef.current.scrollTop = sshTermRef.current.scrollHeight;
    }
  }, [sshOutput, sshAutoScroll]);

  useEffect(() => {
    if (sshConnected && sshConnectedAt) {
      sshTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sshConnectedAt) / 1000);
        const m = Math.floor(elapsed / 60);
        const s = elapsed % 60;
        setSshConnectionTime(`${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
      }, 1000);
    }
    return () => {
      if (sshTimerRef.current) clearInterval(sshTimerRef.current);
    };
  }, [sshConnected, sshConnectedAt]);

  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  useEffect(() => {
    setSshConnected(false);
    setSshOutput('');
  }, [serverId]);

  return (
    <Panel title="SSH Terminal" actions={
      <>
        <span className="flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, color: sshConnecting ? 'var(--warning)' : sshConnected ? 'var(--success)' : sshError ? 'var(--danger)' : 'var(--text-3)' }}>
          <span style={{ width: 8, height: 8, borderRadius: 9999, background: sshConnecting ? 'var(--warning)' : sshConnected ? 'var(--success)' : sshError ? 'var(--danger)' : 'var(--text-3)' }} />
          {sshConnecting ? 'Connecting...' : sshConnected ? `Connected [${sshConnectionTime || '00:00'}]` : sshError ? 'Error' : 'Disconnected'}
        </span>
        <select value={sshFontSize} onChange={e => setSshFontSize(e.target.value)} style={{ height: 30, fontSize: 12 }}>
          <option value="xs">Small</option>
          <option value="sm">Medium</option>
          <option value="base">Large</option>
        </select>
        {sshConnected && (
          <>
            <label className="flex items-center gap-1" style={{ fontSize: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={sshAutoScroll} onChange={e => setSshAutoScroll(e.target.checked)} style={{ width: 12, height: 12 }} />
              Auto
            </label>
            <Button variant="ghost" size="sm" icon="copy" onClick={() => {
              navigator.clipboard.writeText(sshOutput).catch(() => {});
            }} title="Copy output">Copy</Button>
            <Button variant="ghost" size="sm" icon="download" onClick={() => {
              const blob = new Blob([sshOutput], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `ssh-${server?.name || serverId}-${Date.now()}.log`;
              a.click();
              URL.revokeObjectURL(url);
            }} title="Download output">Save</Button>
            <Button variant="ghost" size="sm" icon="trash" onClick={() => setSshOutput('')} title="Clear output">Clear</Button>
          </>
        )}
        {!sshConnected && !sshConnecting ? (
          <Button variant="primary" size="sm" icon="zap" onClick={connectSsh}>Connect</Button>
        ) : (
          <Button variant="danger" size="sm" icon="x" onClick={disconnectSsh} disabled={sshConnecting}>Disconnect</Button>
        )}
      </>
    }>
      {server && !server.has_ssh_credentials && !sshConnected && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, fontSize: 12, background: 'rgba(251,191,36,0.10)', color: 'var(--warning)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Icon name="alert-triangle" size={16} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 500 }}>No SSH credentials configured</div>
            <div style={{ fontSize: 12, marginTop: 2, opacity: 0.8 }}>
              Connection will attempt fallback keys from <code className="mono">/app/ssh_keys/</code>, <code className="mono">/root/.ssh/</code>, and managed SSH keys.<br/>
              To ensure access, <strong>edit the server</strong> or assign a key from the <strong>SSH Keys</strong> page.
            </div>
          </div>
        </div>
      )}

      {sshError && !sshConnected && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, fontSize: 12, background: 'rgba(248,113,113,0.10)', color: 'var(--danger)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Icon name="alert-triangle" size={16} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 500 }}>Connection failed</div>
            <div style={{ fontSize: 12, marginTop: 2, opacity: 0.8 }}>{sshError}</div>
          </div>
        </div>
      )}

      <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #374151' }}>
        <div className="flex items-center justify-between" style={{ padding: '6px 12px', background: '#1f2937', borderBottom: '1px solid #374151' }}>
          <div className="flex items-center gap-1.5">
            <div style={{ width: 10, height: 10, borderRadius: 9999, background: '#ef4444' }}></div>
            <div style={{ width: 10, height: 10, borderRadius: 9999, background: '#eab308' }}></div>
            <div style={{ width: 10, height: 10, borderRadius: 9999, background: '#22c55e' }}></div>
            <span className="mono" style={{ marginLeft: 8, fontSize: 10, color: '#9ca3af' }}>
              {sshConnected ? server?.name || serverId : 'disconnected'}
            </span>
          </div>
          {sshConnected && (
            <span style={{ fontSize: 10, color: '#34d399' }}>{sshConnectionTime || '00:00'}</span>
          )}
        </div>
        <div ref={sshTermRef}
          style={{ background: '#030712', padding: 16, fontFamily: 'var(--mono)', lineHeight: 1.6, color: '#34d399', fontSize: SSH_FONT_SIZES[sshFontSize], minHeight: 380, maxHeight: 480, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
          {sshOutput || (
            <span style={{ color: '#4b5563' }}>
              {sshConnecting ? 'Establishing SSH connection...' : 'Click "Connect" to start an SSH session.'}
            </span>
          )}
          {sshConnected && (
            <span style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite', color: '#34d399' }}>▊</span>
          )}
        </div>

        {sshConnected && (
          <div className="flex items-center" style={{ borderTop: '1px solid #374151', background: '#111827' }}>
            <span className="mono" style={{ paddingLeft: 12, fontSize: 12, color: '#a78bfa', userSelect: 'none' }}>$</span>
            <input ref={sshInputRef} type="text" value={sshInput}
              onChange={e => setSshInput(e.target.value)}
              onKeyDown={handleSshKeyDown}
              style={{ flex: 1, padding: '10px 12px', background: 'transparent', color: '#34d399', fontSize: 14, fontFamily: 'var(--mono)', outline: 'none', border: 'none' }}
              placeholder="Type a command and press Enter..."
              spellCheck={false} autoComplete="off"
            />
            <button onClick={() => sendSshCommand(sshInput)} disabled={!sshInput.trim()}
              style={{ padding: '10px 12px', fontSize: 12, fontWeight: 500, color: '#9ca3af', background: 'none', border: 'none', borderLeft: '1px solid #374151', cursor: 'pointer', opacity: !sshInput.trim() ? 0.3 : 1 }}>
              Send
            </button>
          </div>
        )}
      </div>

      {sshConnected && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Quick Commands</div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { cmd: 'ls -la', label: 'ls -la', group: 'fs' },
              { cmd: 'pwd', label: 'pwd', group: 'fs' },
              { cmd: 'df -h', label: 'df -h', group: 'fs' },
              { cmd: 'du -sh *', label: 'du -sh', group: 'fs' },
              { cmd: 'free -m', label: 'free -m', group: 'sys' },
              { cmd: 'uptime', label: 'uptime', group: 'sys' },
              { cmd: 'top -bn1 | head -20', label: 'top 20', group: 'sys' },
              { cmd: 'ps aux | sort -nrk 4 | head -10', label: 'top mem', group: 'sys' },
              { cmd: 'journalctl -n 30 --no-pager', label: 'journal', group: 'log' },
              { cmd: 'dmesg | tail -20', label: 'dmesg', group: 'log' },
              { cmd: 'ip a', label: 'ip a', group: 'net' },
              { cmd: 'ss -tlnp', label: 'ss -tlnp', group: 'net' },
              { cmd: 'ping -c 3 8.8.8.8', label: 'ping test', group: 'net' },
              { cmd: 'systemctl list-units --type=service --state=running', label: 'services', group: 'svc' },
              { cmd: 'systemctl status --no-pager', label: 'systemctl', group: 'svc' },
              { cmd: 'whoami', label: 'whoami', group: 'info' },
              { cmd: 'hostnamectl', label: 'hostname', group: 'info' },
              { cmd: 'cat /etc/os-release', label: 'os-release', group: 'info' },
            ].map(cmd => (
              <button key={cmd.cmd} onClick={() => sendSshCommand(cmd.cmd)} disabled={!sshConnected}
                className="chip" style={{ fontFamily: 'var(--mono)', opacity: !sshConnected ? 0.4 : 1 }}>
                {cmd.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
